import { findConversation } from './findConversation';
import getFindConversationTraversal from './utils/getFindConversationTraversal';
import BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import FolderId from 'owa-service/lib/contract/FolderId';
import SortResults from 'owa-service/lib/contract/SortResults';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import { tryConsumeFindConversationResponse } from 'owa-session-store/lib/services/sessionDataResponseStash';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { isFeatureEnabled } from 'owa-feature-flags';
import RestrictionType from 'owa-service/lib/contract/RestrictionType';

/**
 * The FindConversationWithStartIndex is used today to load rows when we want to load them from the beginning (or 0th index)
 * This is currently used in load initial rows and in reload scenarios
 * @param folderId - The folderId for which we are fetching the data
 * @param startIndex - The index from where we want to fetch the rows
 * @param rowsToLoad - the number of rows to load
 * @param viewFilter - view filter (e.g - Unread\Flag)
 * @param sortResults - sort by properties
 * @param focusedViewFilter - whether focused view is on
 * @param findConversationShapeName - the shape for the request
 * @param shouldTryToUseSessionDataResponse - indicates if the call should try to use session data find conversation response
 * @param options - optional parameters to be passed to request (method and/or headers)
 * @param categoryName - the specific filter to use based on viewFilter
 * @param hashtagsToInclude the hashtags to include for the request
 * @param hashtagsToExclude the hashtags to exclude for the request
 */
export default function findConversationWithStartIndex(
    baseFolderId: BaseFolderId,
    startIndex: number,
    rowsToLoad: number,
    viewFilter: ViewFilter,
    sortResults: SortResults[],
    focusedViewFilter: FocusedViewFilter,
    findConversationShapeName: string,
    shouldTryToUseSessionDataResponse: boolean,
    options: RequestOptions,
    categoryName: string,
    hashtagsToInclude?: string[],
    hashtagsToExclude?: string[],
    searchFolderId?: BaseFolderId,
    restriction?: RestrictionType,
    refinerRestriction?: RestrictionType
): Promise<{
    findConversationResponseMessage: FindConversationResponseMessage;
    responseInit: RequestOptions;
}> {
    if (shouldTryToUseSessionDataResponse && !isFeatureEnabled('tri-inboxPlusEasyOn')) {
        const sessionDataFindConversationResponse = tryConsumeFindConversationResponse(
            (baseFolderId as FolderId).Id,
            startIndex
        );
        if (sessionDataFindConversationResponse) {
            // Return the session data response if available instead of going to the server
            return Promise.resolve({
                findConversationResponseMessage: sessionDataFindConversationResponse.Body,
                responseInit: {
                    status: 200,
                    statusText: 'OK',
                    headers: new Headers(),
                },
            });
        }
    }

    const paging = indexedPageView({
        BasePoint: 'Beginning',
        Offset: startIndex,
        MaxEntriesReturned: rowsToLoad,
    });

    return findConversation(
        baseFolderId,
        paging,
        viewFilter,
        sortResults,
        focusedViewFilter,
        findConversationShapeName,
        options,
        categoryName,
        getFindConversationTraversal(viewFilter),
        hashtagsToInclude,
        hashtagsToExclude,
        searchFolderId,
        restriction,
        refinerRestriction
    ).then((response: Response) => {
        return response.json().then(json => {
            return {
                findConversationResponseMessage: json.Body,
                responseInit: {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                },
                url: response.url,
            };
        });
    });
}
